import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    IconButton,
    Grid,
    Chip,
    Alert,
    Snackbar
} from '@mui/material';
import { Edit, Delete, Add, Visibility } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    getAffiliateNotifications,
    createNotification,
    updateNotification,
    deleteNotification,
    toggleNotificationActive
} from '../api/getClassesApi';

const Notifications = ({ affiliateId }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingNotification, setEditingNotification] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [notificationToDelete, setNotificationToDelete] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        active: true
    });

    // React-Quill configuration
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'align': [] }],
            ['link'],
            ['clean']
        ],
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'list', 'bullet', 'indent',
        'align',
        'link'
    ];

    useEffect(() => {
        fetchNotifications();
    }, [affiliateId]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const data = await getAffiliateNotifications(affiliateId);
            setNotifications(data);
        } catch (error) {
            showSnackbar('Error fetching notifications', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleDialogOpen = (notification = null) => {
        if (notification) {
            setEditingNotification(notification);
            setFormData({
                title: notification.title,
                content: notification.content,
                active: notification.active
            });
        } else {
            setEditingNotification(null);
            setFormData({
                title: '',
                content: '',
                active: true
            });
        }
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditingNotification(null);
        setFormData({
            title: '',
            content: '',
            active: true
        });
    };

    const handleSubmit = async () => {
        try {
            if (editingNotification) {
                await updateNotification(editingNotification.id, formData);
                showSnackbar('Notification updated successfully');
            } else {
                await createNotification({
                    ...formData,
                    affiliateId: parseInt(affiliateId)
                });
                showSnackbar('Notification created successfully');
            }
            fetchNotifications();
            handleDialogClose();
        } catch (error) {
            showSnackbar('Error saving notification', 'error');
        }
    };

    const handleDelete = async (notificationId) => {
        try {
            await deleteNotification(notificationId);
            showSnackbar('Notification deleted successfully');
            fetchNotifications();
        } catch (error) {
            showSnackbar('Error deleting notification', 'error');
        }
    };

    const handleDeleteClick = (notificationId) => {
        setNotificationToDelete(notificationId);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setNotificationToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        if (!notificationToDelete) return;
        await handleDelete(notificationToDelete);
        handleDeleteCancel();
    };

    const handleEditClick = (notification) => {
        if (deleteConfirmOpen) {
            handleDeleteCancel();
        }
        handleDialogOpen(notification);
    };

    const handleToggleActive = async (notificationId) => {
        try {
            await toggleNotificationActive(notificationId);
            showSnackbar('Notification status updated successfully');
            fetchNotifications();
        } catch (error) {
            showSnackbar('Error updating notification status', 'error');
        }
    };

    // Function to strip HTML tags for display
    const stripHtml = (html) => {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4">Notifications</Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleDialogOpen()}
                    sx={{ backgroundColor: '#d4af37', '&:hover': { backgroundColor: '#b8941f' } }}
                >
                    Add Notification
                </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>How notifications work:</strong>
                </Typography>
                <Typography variant="body2" component="div">
                    • Notifications are automatically shown to members who have this gym set as their home affiliate
                    <br />
                    • They appear when members open the Irontrack page
                    <br />
                    • Each member sees each notification only once
                    <br />
                    • Only active notifications are displayed to members
                    <br />
                    • Perfect for announcements, schedule changes, promotions, or important gym updates
                </Typography>
            </Alert>

            {loading ? (
                <Typography>Loading notifications...</Typography>
            ) : (
                <Grid container spacing={3}>
                    {notifications.map((notification) => (
                        <Grid item xs={12} md={6} key={notification.id}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Typography variant="h6" component="h2">
                                            {notification.title}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditClick(notification)}
                                                color="primary"
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteClick(notification.id)}
                                                color="error"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                        <Chip
                                            label={notification.active ? 'Active' : 'Inactive'}
                                            color={notification.active ? 'success' : 'default'}
                                            size="small"
                                            sx={{ mb: 1 }}
                                        />
                                        <Chip
                                            label={`${notification._count?.userNotifications || 0} views`}
                                            color="info"
                                            size="small"
                                            icon={<Visibility />}
                                            sx={{ ml: 1, mb: 1 }}
                                        />
                                    </Box>

                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        {stripHtml(notification.content).length > 150
                                            ? `${stripHtml(notification.content).substring(0, 150)}...`
                                            : stripHtml(notification.content)}
                                    </Typography>

                                    <Typography variant="caption" color="text.secondary">
                                        Created: {formatDate(notification.createdAt)}
                                    </Typography>

                                    <Box sx={{ mt: 2 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={notification.active}
                                                    onChange={() => handleToggleActive(notification.id)}
                                                />
                                            }
                                            label="Active"
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {notifications.length === 0 && (
                        <Grid item xs={12}>
                            <Typography variant="body1" color="text.secondary" textAlign="center">
                                No notifications yet. Create your first notification to reach your members!
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* Notification Dialog */}
            <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {editingNotification ? 'Edit Notification' : 'Create New Notification'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Notification Title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            fullWidth
                            required
                        />

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Notification Content *
                            </Typography>
                            <Box sx={{ mb: { xs: 8, md: 6 } }}> {/* Extra margin bottom on mobile */}
                                <ReactQuill
                                    theme="snow"
                                    value={formData.content}
                                    onChange={(content) => setFormData({ ...formData, content: content })}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    style={{
                                        height: '300px',
                                        marginBottom: '50px'
                                    }}
                                    placeholder="Enter your notification content here..."
                                />
                            </Box>
                        </Box>

                        <Box sx={{ mt: { xs: 2, md: 0 } }}> {/* Extra top margin on mobile */}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.active}
                                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                    />
                                }
                                label="Active"
                            />
                        </Box>
                    </Box>

                    <Alert severity="info" sx={{ mt: 2 }}>
                        This notification will be shown to users who have this gym set as their home affiliate when they open the training diary.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        sx={{ backgroundColor: '#d4af37', '&:hover': { backgroundColor: '#b8941f' } }}
                    >
                        {editingNotification ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-notification-dialog-title"
                aria-describedby="delete-notification-dialog-description"
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle id="delete-notification-dialog-title">Confirm delete</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-notification-dialog-description">
                        Are you sure you want to delete this notification? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDeleteConfirm} autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Notifications;